
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  IconButton,
  Box,
  Paper,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useDispatch, useSelector } from 'react-redux';
import { getMedicines, addMedicine } from '../apis/medicineSlice';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import SearchClinic from './Autocomplete/SearchClinic';
import { toast } from 'react-toastify';
import { isAdmin, isDoctor } from '../utils/findUserRole';

const createFilterOptions = () => {
  return (options, params) => {
    const filtered = options.filter((opt) => {
      const label = (opt.name || '').toLowerCase();
      return label.includes((params.inputValue || '').toLowerCase());
    });

    if (params.inputValue !== '' && !filtered.some((o) => o.name.toLowerCase() === params.inputValue.toLowerCase())) {
      filtered.push({
        inputValue: params.inputValue,
        name: `Add "${params.inputValue}"`,
        __isNew: true,
      });
    }

    return filtered;
  };
};

export default function MedicineDialog({
  open,
  onClose,
  prescriptions = [],
  setPrescriptions,
  inline = false,
  clinicId: propClinicId = null,
}) {
  const dispatch = useDispatch();
  const { medicines } = useSelector((s) => s.medicineData || { medicines: [] });
  const { loggedIn } = useSelector((s) => s.authData || {});

  const [rows, setRows] = useState([]);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [dosage, setDosage] = useState('');
  const [frequency, setFrequency] = useState('');
  const [instruction, setInstruction] = useState('');
  const [qty, setQty] = useState(''); // ← New state for quantity
  const [editIndex, setEditIndex] = useState(-1);
  const [autoKey, setAutoKey] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createMedicineName, setCreateMedicineName] = useState('');
  const [createMedicineQty, setCreateMedicineQty] = useState(null);
  const [createClinicData, setCreateClinicData] = useState({ clinicId: propClinicId || loggedIn?.user?.clinicId || null });

  useEffect(() => {
    dispatch(getMedicines());
  }, [dispatch]);

  // Autofill clinic when create dialog opens if user has clinicId
  useEffect(() => {
    if (!(isAdmin() || isDoctor())) return;
    if (createDialogOpen && !propClinicId) {
      const userClinicId = loggedIn?.clinicId;


      if (userClinicId) {
        setCreateClinicData({ clinicId: userClinicId });
      }
    }
  }, [createDialogOpen, propClinicId, loggedIn?.clinicId]);

  useEffect(() => {
    setRows(
      (prescriptions || []).map((p, idx) => ({
        id: idx + 1,
        order: p.order ?? idx + 1,
        qty: p.qty || '', // preserve existing qty
        ...p,
      })),
    );
  }, [prescriptions]);

  const resetInputs = () => {
    setSelectedMedicine(null);
    setDosage('');
    setFrequency('');
    setInstruction('');
    setQty(''); // reset qty
    setEditIndex(-1);
  };

  const handleMedicineChange = (event, newValue) => {
    if (typeof newValue === 'string') {
      // User typed and pressed Enter
      setTimeout(() => {
        setCreateMedicineName(newValue);
        setCreateDialogOpen(true);
      });
    } else if (newValue && newValue.__isNew) {
      // User clicked "Add ..." option
      setCreateMedicineName(newValue.inputValue || '');
      setCreateDialogOpen(true);
    } else if (newValue && newValue.inputValue) {
      // Free solo input
      setCreateMedicineName(newValue.inputValue);
      setCreateDialogOpen(true);
    } else if (newValue) {
      setSelectedMedicine(newValue);
    } else {
      setSelectedMedicine(null);
    }
  };

  const handleCreateMedicineSubmit = async (event) => {
    event.preventDefault();

    if (!createMedicineName.trim()) {
      toast.error('Medicine name is required');
      return;
    }

    if (!createClinicData.clinicId) {
      toast.error('Clinic is required');
      return;
    }

    const payload = {
      name: createMedicineName.trim(),
      clinicId: createClinicData.clinicId,
      qty: createMedicineQty ? Number(createMedicineQty) : 0,
    };

    const response = await dispatch(addMedicine(payload));

    // backend returns { message, data } (no success flag)
    if (response?.payload?.data) {
      toast.success('Medicine created successfully');
      handleCloseCreateDialog();
      await dispatch(getMedicines());
      setSelectedMedicine(response.payload.data);
      setAutoKey((k) => k + 1);
    } else {
      toast.error(response?.payload?.message || 'Error creating medicine');
    }
  };

  const handleCloseCreateDialog = () => {
    setCreateMedicineName('');
    setCreateMedicineQty(null);
    setCreateClinicData({ clinicId: propClinicId || loggedIn?.user?.clinicId || null });
    setCreateDialogOpen(false);
  };

  const handleAdd = () => {
    if (!selectedMedicine || !selectedMedicine._id) return;

    if(!qty || isNaN(qty) || Number(qty) == 0) {
      toast.error("Please enter a valid quantity (1 or more).");
      return;
    }

    const item = {
      medicine: { _id: selectedMedicine._id, name: selectedMedicine.name },
      medicineId: selectedMedicine._id,
      name: selectedMedicine.name,
      dosage,
      frequency,
      instruction,
      qty: qty || '', // include qty (can be empty string if not filled)
      order: rows.length + 1,
    };

    let newRows = [];
    if (editIndex > -1) {
      newRows = rows.map((r, idx) => (idx === editIndex ? { ...r, ...item } : r));
    } else {
      newRows = [...rows, { id: rows.length + 1, ...item }];
    }

    newRows = newRows.map((r, idx) => ({ ...r, id: idx + 1, order: idx + 1 }));

    setRows(newRows);
    setPrescriptions(
      newRows.map((r) => ({
        medicine: r.medicine,
        medicineId: r.medicineId,
        name: r.name,
        dosage: r.dosage,
        frequency: r.frequency,
        instruction: r.instruction,
        qty: r.qty,
        order: r.order,
      })),
    );
    resetInputs();
  };

  const handleEdit = (row) => {
    const idx = rows.findIndex((r) => r.id === row.id);
    if (idx === -1) return;
    setEditIndex(idx);
    setSelectedMedicine({ _id: row.medicineId, name: row.name });
    setDosage(row.dosage || '');
    setFrequency(row.frequency || '');
    setInstruction(row.instruction || '');
    setQty(row.qty || ''); // ← load qty for editing
  };

  const handleDelete = (row) => {
    const newRows = rows
      .filter((r) => r.id !== row.id)
      .map((r, idx) => ({ ...r, id: idx + 1, order: idx + 1 }));
    setRows(newRows);
    setPrescriptions(
      newRows.map((r) => ({
        medicine: r.medicine,
        medicineId: r.medicineId,
        name: r.name,
        dosage: r.dosage,
        frequency: r.frequency,
        instruction: r.instruction,
        qty: r.qty,
        order: r.order,
      })),
    );
  };

  const move = (index, direction) => {
    const newRows = [...rows];
    const target = index + direction;
    if (target < 0 || target >= newRows.length) return;
    [newRows[index], newRows[target]] = [newRows[target], newRows[index]];
    const normalized = newRows.map((r, idx) => ({ ...r, id: idx + 1, order: idx + 1 }));
    setRows(normalized);
    setPrescriptions(
      normalized.map((r) => ({
        medicine: r.medicine,
        medicineId: r.medicineId,
        name: r.name,
        dosage: r.dosage,
        frequency: r.frequency,
        instruction: r.instruction,
        qty: r.qty,
        order: r.order,
      })),
    );
  };

  const handleClose = () => {
    setPrescriptions(
      rows.map((r) => ({
        medicine: r.medicine,
        medicineId: r.medicineId,
        name: r.name,
        dosage: r.dosage,
        frequency: r.frequency,
        instruction: r.instruction,
        qty: r.qty,
        order: r.order,
      })),
    );
    if (onClose) onClose();
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newRows = Array.from(rows);
    const [removed] = newRows.splice(result.source.index, 1);
    newRows.splice(result.destination.index, 0, removed);
    const normalized = newRows.map((r, idx) => ({ ...r, id: idx + 1, order: idx + 1 }));
    setRows(normalized);
    setPrescriptions(
      normalized.map((r) => ({
        medicine: r.medicine,
        medicineId: r.medicineId,
        name: r.name,
        dosage: r.dosage,
        frequency: r.frequency,
        instruction: r.instruction,
        qty: r.qty,
        order: r.order,
      })),
    );
  };

  const panelContent = (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Autocomplete
          key={autoKey}
          options={medicines || []}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.name || '')}
          value={selectedMedicine}
          onChange={handleMedicineChange}
          freeSolo
          filterOptions={createFilterOptions()}
          sx={{ width: 300 }}
          renderInput={(params) => <TextField {...params} label="Select or Create Medicine" />}
        />
        <Typography sx={{ minWidth: 140, color: 'text.secondary' }}>
          Available: {selectedMedicine && selectedMedicine.qty != null ? selectedMedicine.qty : '-'}
        </Typography>

        <TextField
          label="Dosage"
          value={dosage}
          onChange={(e) => setDosage(e.target.value)}
          sx={{ width: 140 }}
        />

        <TextField
          label="Frequency"
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          sx={{ width: 140 }}
        />

        <TextField
          label="Quantity"
          type="number"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          inputProps={{ min: 0 }}
          sx={{ width: 120 }}
        />

        <TextField
          label="Instruction"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />

        <Button variant="contained" onClick={handleAdd}>
          {editIndex > -1 ? 'Update' : 'Add'}
        </Button>
      </Box>

      <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog}>
        <form onSubmit={handleCreateMedicineSubmit}>
          <DialogTitle>Create Medicine</DialogTitle>
          <DialogContent>
            <SearchClinic
              open={createDialogOpen}
              setData={setCreateClinicData}
              data={createClinicData}
              name="clinicId"
              label="Select Clinic"
              variant="standard"
              size="small"
              disabled={!!propClinicId}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Medicine Name"
              fullWidth
              variant="standard"
              value={createMedicineName}
              onChange={(e) => setCreateMedicineName(e.target.value)}
            />
            <TextField
              autoFocus
              margin="dense"
              label="Medicine Qty"
              fullWidth
              variant="standard"
              value={createMedicineQty}
              onChange={(e) => setCreateMedicineQty(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              Create
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Header Row */}
      <Paper
        elevation={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          mb: 1,
          backgroundColor: '#f5f5f5',
          fontWeight: 600,
        }}
      >
        <Typography sx={{ width: 40 }}>#</Typography>
        <Typography sx={{ width: 230 }}>Medicine</Typography>
        <Typography sx={{ width: 70 }}>Dosage</Typography>
        <Typography sx={{ width: 80 }}>Frequency</Typography>
        <Typography sx={{ width: 60, textAlign: 'center' }}>Qty</Typography>
        <Typography sx={{ flex: 1 }}>Instruction</Typography>
        <Typography sx={{ width: 160, textAlign: 'center' }}>Actions</Typography>
      </Paper>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="prescriptions">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {rows.map((row, index) => (
                <Draggable key={row.id} draggableId={`r-${row.id}`} index={index}>
                  {(prov) => (
                    <Paper
                      ref={prov.innerRef}
                      {...prov.draggableProps}
                      {...prov.dragHandleProps}
                      sx={{ display: 'flex', alignItems: 'center', p: 1, mb: 1 }}
                    >
                      <Typography sx={{ width: 40 }}>{row.order}</Typography>
                      <Typography sx={{ width: 230 }}>{row.name}</Typography>
                      <Typography sx={{ width: 70 }}>{row.dosage}</Typography>
                      <Typography sx={{ width: 80 }}>{row.frequency}</Typography>
                      <Typography sx={{ width: 60, textAlign: 'center' }}>
                        {row.qty || '-'}
                      </Typography>
                      <Typography
                        sx={{
                          flex: 1,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {row.instruction}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton onClick={() => handleEdit(row)} size="small">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(row)} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                        <IconButton onClick={() => move(index, -1)} size="small">
                          <ArrowUpwardIcon />
                        </IconButton>
                        <IconButton onClick={() => move(index, 1)} size="small">
                          <ArrowDownwardIcon />
                        </IconButton>
                      </Box>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );

  if (inline) {
    return panelContent;
  }

  return (
    <Dialog open={open} fullWidth maxWidth="lg" onClose={handleClose}>
      <DialogTitle>Prescribe Medicine</DialogTitle>
      <DialogContent dividers>{panelContent}</DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Save & Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}